import React, { useState } from 'react';
import { Button, CardActions, CardContent, CardHeader } from '@mui/material';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
import Rating from '@mui/material/Rating';
import Grid from '@mui/material/Grid';
import './styles.css';

interface IReview {
  id: number;
  name: string | undefined;
  photo: string | null | undefined;
  rating: number | null;
  datetime: string | null;
  review: string | null;
}

// maximum review limit to be shown at the panel
const MAX_LIMIT = 10;

const Review: React.FC<IReview> = ({
  name,
  photo,
  rating,
  review,
  datetime,
}) => {
  return (
    <Card sx={{ height: '100%', width: '100%' }}>
      <CardHeader
        avatar={
          <Avatar alt={name} src={photo ?? ''} sx={{ width: 56, height: 56 }} />
        }
        title={name}
        subheader={datetime}
      />
      {review && (
        <CardContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {review}
          </Typography>
        </CardContent>
      )}
      <CardActions disableSpacing className="rating_section">
        <Rating name="size-medium" readOnly defaultValue={rating ?? 0} />
      </CardActions>
    </Card>
  );
};

const Reviews: React.FC = () => {
  const isMounted = React.useRef<boolean>(false);
  const [reviews, setReviews] = useState<IReview[]>([]);

  const initReviews = async () => {
    const { Place } = (await google.maps.importLibrary(
      'places'
    )) as google.maps.PlacesLibrary;

    // fetch `manel travels & leisure` place
    const place = new Place({
      id: 'ChIJeX4K_c1X4joRJgugkuA52W0',
    });
    // fetch reviews
    await place.fetchFields({
      fields: ['reviews'],
    });
    const reviewsList =
      place?.reviews?.map?.<IReview>((data) => ({
        id: data.publishTime?.getTime?.() ?? new Date().getTime(),
        name: data.authorAttribution?.displayName,
        photo: data.authorAttribution?.photoURI,
        rating: data.rating,
        review: data.text,
        datetime: data.relativePublishTimeDescription,
      })) ?? [];
    // set to reviews list
    setReviews(reviewsList.splice(0, MAX_LIMIT));
  };

  React.useEffect(() => {
    if (!isMounted.current) {
      initReviews();
    }
    isMounted.current = true;
  }, []);

  const noReviews = (
    <Typography gutterBottom variant="h5" sx={{ marginBottom: 2 }} className="">
      No reviews yet
    </Typography>
  );

  return (
    <Container
      maxWidth="lg"
      sx={{ marginTop: 5, marginBottom: 10 }}
      className="reviews_backdrop"
    >
      <Typography
        gutterBottom
        variant="h3"
        sx={{ marginBottom: 5 }}
        className="title"
      >
        Reviews
      </Typography>
      <Grid container spacing={6}>
        {reviews
          ? reviews.map((review) => (
              <Grid key={review.id} item xs={12} sm={6} md={4} lg={4}>
                <Review {...review} />
              </Grid>
            ))
          : noReviews}
      </Grid>
      <Button
        variant="contained"
        size="medium"
        className="review_button"
        onClick={() => window.open('https://g.page/r/CSYLoJLgOdltEAI/review')}
      >
        Leave a review
      </Button>
    </Container>
  );
};

export default Reviews;
